"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { DICTIONARIES } from "./dictionaries";
import { DEFAULT_LOCALE } from "./locales";

const STORAGE_KEY = "hy-locale";

type Vars = Record<string, string | number>;

interface LocaleContextValue {
  locale: string;
  setLocale: (locale: string) => void;
  t: (key: string, vars?: Vars) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function lookup(dict: Record<string, unknown>, key: string): unknown {
  return key.split(".").reduce<unknown>((node, part) => {
    if (node && typeof node === "object" && part in node) {
      return (node as Record<string, unknown>)[part];
    }
    return undefined;
  }, dict);
}

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name) =>
    name in vars ? String(vars[name]) : match
  );
}

function translate(locale: string, key: string, vars?: Vars): string {
  const primary = lookup(DICTIONARIES[locale] ?? {}, key);
  const value = typeof primary === "string" ? primary : lookup(DICTIONARIES[DEFAULT_LOCALE], key);
  if (typeof value !== "string") {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[i18n] missing translation key: "${key}"`);
    }
    return key;
  }
  return interpolate(value, vars);
}

// Detects the visitor's preferred language client-side rather than via middleware/headers --
// this site is fully static (see next.config.ts's CSP comment on why there's no middleware),
// so there's no request to inspect Accept-Language on. Read via useSyncExternalStore rather
// than a useEffect+setState: the server snapshot returns DEFAULT_LOCALE (matching what was
// statically generated), and React reconciles to the real client snapshot right after
// hydration -- same reasoning as CookieConsent.tsx's localStorage read, and it sidesteps the
// setState-in-effect footgun a manual detect-on-mount effect would hit.
function noopSubscribe() {
  return () => {};
}

function getServerLocale(): string {
  return DEFAULT_LOCALE;
}

function getClientLocale(): string {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && stored in DICTIONARIES) return stored;

  for (const lang of window.navigator.languages ?? [window.navigator.language]) {
    const code = lang.split("-")[0];
    if (code in DICTIONARIES) return code;
  }
  return DEFAULT_LOCALE;
}

export default function LocaleProvider({ children }: { children: ReactNode }) {
  const detectedLocale = useSyncExternalStore(noopSubscribe, getClientLocale, getServerLocale);
  // A user's explicit choice (via LanguageSwitcher) overrides detection until reload.
  const [chosenLocale, setChosenLocale] = useState<string | null>(null);
  const locale = chosenLocale ?? detectedLocale;

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  function setLocale(next: string) {
    window.localStorage.setItem(STORAGE_KEY, next);
    setChosenLocale(next);
  }

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => translate(locale, key, vars),
    }),
    [locale]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useTranslations() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useTranslations must be used within LocaleProvider");
  return ctx;
}
